
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { handleErrorMessage } from "./component/ErrorDialog";

type AudioRecorderOptions = {
    setErrorMessage: (newErrorMessage: string) => void;
    onStop?: (audioData: Blob) => void;
};

const supportedMimeTypes = [
    'audio/webm',
    'audio/wav',
    'audio/vnd.wav',
    'audio/mp4',        // less likely to be supported
].filter(Boolean) as string[];

export const useAudioRecorder = ({ setErrorMessage, onStop }: AudioRecorderOptions) => {

    const history = useHistory();
    const location = useLocation();

    const streamRef = React.useRef<MediaStream | null>(null);
    const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
    const audioDataList = React.useRef<Blob[]>([]);
    const onStopRef = React.useRef(onStop);
    const mountedRef = React.useRef(true);

    const [isRecording, setIsRecording] = React.useState(false);

    React.useEffect(() => {
        onStopRef.current = onStop;
    }, [onStop]);

    const mimeType = supportedMimeTypes.find(type => MediaRecorder.isTypeSupported(type));
    if (!mimeType) {
        throw new Error('No supported audio MIME type found for MediaRecorder.');
    }

    const startRecording = React.useCallback(async () => {
        try {
            setIsRecording(false); // Reset state first
            audioDataList.current = [];

            // Stop and clean up any old stream
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }

            // Request a new audio stream
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!mountedRef.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream, { mimeType });

            mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data.size > 0) {
                    audioDataList.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(audioDataList.current, { type: mimeType });
                if (mountedRef.current && onStopRef.current && blob.size > 0) {
                    onStopRef.current(blob);
                }
                audioDataList.current = [];
            };

            mediaRecorder.start();
            mediaRecorderRef.current = mediaRecorder;

            setIsRecording(true);
        } catch (error: any) {
            let message = 'Unable to access microphone.';

            if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                message += '\n\nReason: No microphone was found on this device.';
                message += '\n\nChecklist:\n1. Is a microphone physically connected?\n2. Are audio input devices recognized by your system?';
            } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                message += '\n\nReason: Permission to access the microphone was denied.';
                message += '\n\nChecklist:\n1. Is Chrome allowed to use your microphone? (chrome://settings/content/microphone)\n2. Did you block access by mistake in the permission popup?\n3. Is this site listed as allowed in Chrome permissions?\n4. Try refreshing the page or restarting Chrome.';
            } else if (error.name === 'NotReadableError') {
                message += '\n\nReason: Microphone is already in use by another application.';
            } else if (error.name === 'OverconstrainedError') {
                message += '\n\nReason: The requested device settings are not supported.';
            } else {
                message += `\n\nUnexpected error: ${error.message || error}`;
            }
            handleErrorMessage(message, setErrorMessage, history, location);
        }
    }, [mimeType, setErrorMessage, history, location]);

    const stopRecording = React.useCallback(() => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        } finally {
            setIsRecording(false);
        }
    }, [setErrorMessage, history, location]);

    // Clean up on unmount
    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
        };
    }, []);

    return {
        startRecording,
        stopRecording,
        isRecording
    };
};
